import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import { t, tx } from '@/translations/translations';
import { useProducts } from '@/hooks/useProducts';

/* Hero carousel — curated photos, 1 per product family with model focus.
 * Prices come live from the products store via `productId`, so any price
 * update in seed_data / admin propagates here automatically. */
const CAROUSEL_ITEMS = [
  { img: '/products/kol-haposkim-natural-front.webp', es: 'Tzitzit Lana Kol Haposkim', en: 'Wool Kol Haposkim', he: 'ציצית צמר כל הפוסקים', productId: 1, softTech: true },
  { img: '/products/cotton-tank-kid-front.webp', es: 'Tzitzit Algodón Tank Niño', en: 'Cotton Tank Kids', he: 'ציצית כותנה גופייה לילד', productId: 5 },
  { img: '/products/cotton-tank-model-adult.webp', es: 'Tzitzit Algodón Tank', en: 'Cotton Tank Adult', he: 'ציצית כותנה גופייה', productId: 5 },
  { img: '/products/wool-tank-natural-model.webp', es: 'Tzitzit Lana Tank', en: 'Wool Tank', he: 'ציצית צמר גופייה', productId: 2 },
  { img: '/products/wool-tshirt-black-model2.webp', es: 'TZI-SHIRT Negro', en: 'Black TZI-SHIRT', he: 'TZI-SHIRT שחור', productId: 3, priceOverride: 500 },
  { img: '/products/hero-tzishirt-white-profile.webp', es: 'TZI-SHIRT Blanco', en: 'White TZI-SHIRT', he: 'TZI-SHIRT לבן', productId: 3, priceOverride: 500 },
  { img: '/products/hero-tzishirt-bamboo-gray.webp', es: 'TZI-SHIRT Bambú Gris', en: 'Bamboo TZI-SHIRT Gray', he: 'TZI-SHIRT במבוק אפור', productId: 8 },
  { img: '/products/talit-gadol-model3.webp', es: 'Talit Gadol', en: 'Wool Talit Gadol', he: 'טלית גדול', productId: 4 },
  { img: '/products/hero-dryfit-blue-sitting.webp', es: 'TZI-SHIRT Dry-Fit Azul', en: 'Dry-Fit Blue', he: 'TZI-SHIRT דריי-פיט כחול', productId: 9 },
  { img: '/products/dryfit-blacksport-sitting.webp', es: 'TZI-SHIRT SPORT', en: 'TZI-SHIRT SPORT', he: 'TZI-SHIRT ספורט', productId: 9 },
  { img: '/products/hero-mitzva-tee-profile.webp', es: 'Mitzva Tee', en: 'Mitzva Tee', he: 'Mitzva Tee', productId: 11 },
];

const Hero = () => {
  const { language } = useLanguage();
  const { products } = useProducts();
  const trackRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const itemsCount = CAROUSEL_ITEMS.length;

  /* Map productId -> { price, currency } for live price lookup */
  const priceById = useMemo(() => {
    const map = {};
    (products || []).forEach(p => { map[p.id] = { price: p.price, currency: p.currency }; });
    return map;
  }, [products]);

  const formatPrice = (productId, override) => {
    const currency = priceById[productId]?.currency || 'MXN';
    const price = override != null ? override : priceById[productId]?.price;
    if (price == null) return '';
    return `$${Math.round(price).toLocaleString(language === 'es' ? 'es-MX' : language === 'he' ? 'he-IL' : 'en-US')} ${currency}`;
  };

  const scrollToProducts = () => {
    document.getElementById('productos')?.scrollIntoView({ behavior: 'smooth' });
  };

  /* Auto-advance carousel */
  useEffect(() => {
    if (!isAutoPlay) return;
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % itemsCount);
    }, 3000);
    return () => clearInterval(timer);
  }, [isAutoPlay, itemsCount]);

  const goTo = (idx) => {
    setCurrentIndex(idx);
    setIsAutoPlay(false);
    setTimeout(() => setIsAutoPlay(true), 8000);
  };

  const prev = () => goTo((currentIndex - 1 + itemsCount) % itemsCount);
  const next = () => goTo((currentIndex + 1) % itemsCount);

  /* Calculate visible items for desktop carousel — show 5 at a time */
  const getVisibleItems = () => {
    const items = [];
    for (let offset = -2; offset <= 2; offset++) {
      const idx = (currentIndex + offset + itemsCount) % itemsCount;
      items.push({ ...CAROUSEL_ITEMS[idx], offset, idx });
    }
    return items;
  };

  /* Mobile: show 3 */
  const getMobileItems = () => {
    const items = [];
    for (let offset = -1; offset <= 1; offset++) {
      const idx = (currentIndex + offset + itemsCount) % itemsCount;
      items.push({ ...CAROUSEL_ITEMS[idx], offset, idx });
    }
    return items;
  };

  return (
    <section id="hero" className="relative pt-14 sm:pt-16 bg-slate-950 overflow-hidden" data-testid="hero-section">
      <div className="flex flex-col items-center md:min-h-[calc(100vh-4rem)] justify-center py-6 md:py-8 lg:py-12">

        {/* ─── LOGO ─── */}
        <div className="mb-2 lg:mb-4 hero-fade-up">
          <img
            src="/products/mitzva-logo.webp"
            alt="Mitzva Fit"
            className="w-24 sm:w-28 lg:w-32 h-auto invert opacity-90"
            data-testid="hero-logo"
          />
        </div>

        {/* ─── SLOGAN ─── */}
        <div className="text-center mb-1 lg:mb-2 hero-fade-up" style={{ animationDelay: '0.1s' }}>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-white tracking-tight leading-none">
            {t(language, 'hero.title1')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-cyan-300 to-blue-400">
              {t(language, 'hero.title2')}
            </span>
          </h1>
        </div>

        {/* ─── TAGLINE ─── */}
        <p className="text-slate-500 text-[10px] sm:text-xs lg:text-sm tracking-[0.15em] uppercase mb-4 sm:mb-6 lg:mb-8 hero-fade-up" style={{ animationDelay: '0.2s' }}>
          {t(language, 'hero.tagline')}
        </p>

        {/* ─── PRODUCT CAROUSEL (Desktop) ─── */}
        <div className="hidden md:block w-full relative mb-4 lg:mb-6 hero-fade-up" style={{ animationDelay: '0.35s' }}>
          <div className="relative flex items-center justify-center h-[280px] lg:h-[320px]" ref={trackRef}>
            {getVisibleItems().map((item) => {
              const isCenter = item.offset === 0;
              const isAdjacent = Math.abs(item.offset) === 1;
              const scale = isCenter ? 1 : isAdjacent ? 0.82 : 0.65;
              const opacity = isCenter ? 1 : isAdjacent ? 0.7 : 0.35;
              const zIndex = isCenter ? 30 : isAdjacent ? 20 : 10;
              const translateX = item.offset * 260;

              return (
                <div
                  key={`${item.idx}-${item.offset}`}
                  className="absolute cursor-pointer"
                  style={{
                    transform: `translateX(${translateX}px) scale(${scale})`,
                    opacity,
                    zIndex,
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  onClick={() => { if (!isCenter) goTo(item.idx); else scrollToProducts(); }}
                >
                  <div className={`group w-[210px] lg:w-[240px] rounded-2xl overflow-hidden relative ${isCenter ? 'shadow-2xl shadow-cyan-500/10 ring-1 ring-cyan-400/20' : ''} ${item.softTech && isCenter ? 'ring-cyan-400/40 shadow-cyan-500/20' : ''}`}>
                    {/* Soft Tech Badge */}
                    {item.softTech && (
                      <div className="absolute top-2 left-2 z-20 inline-flex items-center gap-1 bg-cyan-500/90 backdrop-blur-sm rounded-full px-2.5 py-1 shadow-lg shadow-cyan-500/30">
                        <Sparkles className="h-3 w-3 text-white" />
                        <span className="text-[10px] font-bold text-white tracking-wider uppercase">Soft Tech Wool</span>
                      </div>
                    )}
                    {/* Product Image — models fill (4:5), flat-lays show full on white */}
                    <div className={`aspect-[4/5] relative overflow-hidden ${item.flat ? 'bg-white' : 'bg-slate-100'}`}>
                      {item.flat ? (
                        <img
                          src={item.img}
                          alt={tx(language, item.es, item.en, item.he)}
                          className="absolute inset-0 w-full h-full object-contain"
                          loading="eager"
                        />
                      ) : (
                        <img
                          src={item.img}
                          alt={tx(language, item.es, item.en, item.he)}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="eager"
                        />
                      )}
                      {/* Gradient overlay for floating text */}
                      <div className="absolute inset-x-0 bottom-0 h-[40%] bg-gradient-to-t from-black/85 via-black/50 to-transparent pointer-events-none" />
                      {/* Floating Info — slides up on hover (center card only) */}
                      <div className={`absolute inset-x-0 bottom-0 px-3 pb-3 pt-6 z-10 transition-all duration-500 ${isCenter ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-95'}`}>
                        <p className="text-sm lg:text-base font-bold text-white truncate drop-shadow-lg">
                          {tx(language, item.es, item.en, item.he)}
                        </p>
                        <p className={`text-xs lg:text-sm font-semibold mt-0.5 drop-shadow-lg ${item.softTech ? 'text-cyan-300' : 'text-cyan-400'}`}>
                          {formatPrice(item.productId, item.priceOverride)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Nav arrows */}
          <button
            onClick={prev}
            className="absolute left-4 lg:left-12 top-1/2 -translate-y-1/2 z-40 w-10 h-10 rounded-full bg-slate-800/80 border border-slate-700 flex items-center justify-center text-white hover:bg-slate-700 transition-colors"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={next}
            className="absolute right-4 lg:right-12 top-1/2 -translate-y-1/2 z-40 w-10 h-10 rounded-full bg-slate-800/80 border border-slate-700 flex items-center justify-center text-white hover:bg-slate-700 transition-colors"
            data-testid="carousel-next"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* ─── PRODUCT CAROUSEL (Mobile) ─── */}
        <div className="md:hidden w-full relative mb-4 hero-fade-up" style={{ animationDelay: '0.35s' }}>
          <div className="relative flex items-center justify-center h-[240px]">
            {getMobileItems().map((item) => {
              const isCenter = item.offset === 0;
              const scale = isCenter ? 1 : 0.78;
              const opacity = isCenter ? 1 : 0.5;
              const zIndex = isCenter ? 30 : 10;
              const translateX = item.offset * 155;

              return (
                <div
                  key={`m-${item.idx}-${item.offset}`}
                  className="absolute"
                  style={{
                    transform: `translateX(${translateX}px) scale(${scale})`,
                    opacity,
                    zIndex,
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  onClick={() => { if (!isCenter) goTo(item.idx); else scrollToProducts(); }}
                >
                  <div className={`w-[170px] rounded-xl overflow-hidden relative ${isCenter ? 'shadow-xl shadow-cyan-500/10 ring-1 ring-cyan-400/20' : ''} ${item.softTech && isCenter ? 'ring-cyan-400/40' : ''}`}>
                    {item.softTech && (
                      <div className="absolute top-1.5 left-1.5 z-10 inline-flex items-center gap-1 bg-cyan-500/90 backdrop-blur-sm rounded-full px-2 py-0.5 shadow-lg shadow-cyan-500/30">
                        <Sparkles className="h-2.5 w-2.5 text-white" />
                        <span className="text-[8px] font-bold text-white tracking-wider uppercase">Soft Tech Wool</span>
                      </div>
                    )}
                    <div className={`aspect-[4/5] relative overflow-hidden ${item.flat ? 'bg-white' : 'bg-slate-100'}`}>
                      {item.flat ? (
                        <img
                          src={item.img}
                          alt={tx(language, item.es, item.en, item.he)}
                          className="absolute inset-0 w-full h-full object-contain"
                          loading="eager"
                        />
                      ) : (
                        <img
                          src={item.img}
                          alt={tx(language, item.es, item.en, item.he)}
                          className="absolute inset-0 w-full h-full object-cover"
                          loading="eager"
                        />
                      )}
                      <div className="absolute inset-x-0 bottom-0 h-[40%] bg-gradient-to-t from-black/85 via-black/50 to-transparent pointer-events-none" />
                      <div className="absolute inset-x-0 bottom-0 px-2.5 pb-2 pt-5 z-10">
                        <p className="text-xs font-bold text-white truncate drop-shadow-lg">
                          {tx(language, item.es, item.en, item.he)}
                        </p>
                        <p className={`text-[10px] font-semibold mt-0.5 drop-shadow-lg ${item.softTech ? 'text-cyan-300' : 'text-cyan-400'}`}>
                          {formatPrice(item.productId, item.priceOverride)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile nav arrows */}
          <button
            onClick={prev}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-40 w-8 h-8 rounded-full bg-slate-800/80 border border-slate-700 flex items-center justify-center text-white"
            data-testid="carousel-prev-mobile"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={next}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-40 w-8 h-8 rounded-full bg-slate-800/80 border border-slate-700 flex items-center justify-center text-white"
            data-testid="carousel-next-mobile"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* ─── DOTS ─── */}
        <div className="flex items-center gap-1.5 mb-3 lg:mb-5 hero-fade-up" style={{ animationDelay: '0.45s' }}>
          {CAROUSEL_ITEMS.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`rounded-full transition-all duration-300 ${
                i === currentIndex
                  ? 'w-6 h-2 bg-cyan-400'
                  : 'w-2 h-2 bg-slate-700 hover:bg-slate-600'
              }`}
              data-testid={`carousel-dot-${i}`}
            />
          ))}
        </div>

        {/* ─── CTA ─── */}
        <div className="flex items-center gap-4 hero-fade-up" style={{ animationDelay: '0.55s' }}>
          <Button
            data-testid="hero-ver-productos-btn"
            className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-6 sm:px-8 py-4 sm:py-6 rounded-full transition-all duration-300 text-sm lg:text-base shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40"
            onClick={scrollToProducts}
          >
            {t(language, 'hero.btnProducts')}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
